/**
 * What of a page's query string may reach analytics. OAuth callbacks, magic links and checkout
 * returns carry one-time codes and tokens in the query, and no name or value heuristic catches
 * them all (`accessToken`, `authCode`, a short opaque `ref`). So the rule is an allowlist: only
 * campaign tags, ad-platform click ids and the few filters the app's own pages read survive;
 * every other parameter, and the fragment, is dropped. Keys match exactly, case included.
 */
const ALLOWED_KEYS = new Set([
  // Ad-platform click ids (attribution needs them whole; they can run past 100 characters).
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  'yclid',
  'rdt_cid',
  // Filters read by the customer app's pages.
  'year',
  'tab',
  'view',
  'type',
  'step',
  'month',
  'connected',
])
const ALLOWED_KEY_PREFIX = 'utm_'
/** A longer value is not a campaign tag or a filter, whatever its key says. */
const MAX_VALUE_LENGTH = 512

export function scrubAnalyticsSearch(search: string | null | undefined): string {
  if (!search) return ''
  const kept = new URLSearchParams()
  for (const [key, value] of new URLSearchParams(search)) {
    if (!ALLOWED_KEYS.has(key) && !key.startsWith(ALLOWED_KEY_PREFIX)) continue
    if (value.length > MAX_VALUE_LENGTH) continue
    kept.append(key, value)
  }
  return kept.toString()
}

/** The same rule for a whole url (a referrer included); the fragment goes too, since fragments carry implicit-flow tokens. */
export function scrubAnalyticsUrl(href: string): string {
  try {
    const url = new URL(href)
    url.search = scrubAnalyticsSearch(url.search)
    url.hash = ''
    return url.toString()
  } catch {
    return href.split(/[?#]/, 1)[0] ?? ''
  }
}

const HTTP_URL = /^(?:https?:)?\/\//i
/** A same-origin path that carries a query or fragment, as an autocaptured relative `href` does. */
const RELATIVE_URL_WITH_QUERY = /^\/(?!\/)[^\s?#]*[?#]/

function scrubRelativeUrl(value: string): string {
  const cut = value.search(/[?#]/)
  const path = value.slice(0, cut)
  if (value[cut] !== '?') return path
  const hash = value.indexOf('#', cut)
  const scrubbed = scrubAnalyticsSearch(value.slice(cut, hash === -1 ? undefined : hash))
  return scrubbed ? `${path}?${scrubbed}` : path
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function scrubValue(key: string | null, value: unknown, seen: WeakMap<object, unknown>): unknown {
  if (typeof value === 'string') {
    if (value === '') return value
    if (key === 'current_url_search') {
      const scrubbed = scrubAnalyticsSearch(value)
      return scrubbed ? `?${scrubbed}` : ''
    }
    if (HTTP_URL.test(value)) return scrubAnalyticsUrl(value)
    if (RELATIVE_URL_WITH_QUERY.test(value)) return scrubRelativeUrl(value)
    return value
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) return seen.get(value)
    const out: unknown[] = []
    seen.set(value, out)
    for (const item of value) out.push(scrubValue(null, item, seen))
    return out
  }
  if (isPlainObject(value)) {
    if (seen.has(value)) return seen.get(value)
    const out: Record<string, unknown> = {}
    seen.set(value, out)
    for (const [childKey, child] of Object.entries(value)) out[childKey] = scrubValue(childKey, child, seen)
    return out
  }
  return value
}

/**
 * mixpanel-browser attaches `$current_url`, `$referrer`, `current_url_search` and the persisted
 * `$initial_referrer` from `window.location` / `document.referrer` on its own, and autocapture adds
 * link targets, both as `$el_attr__href` and nested in `$elements[]` / `$target` as `$attr-href`.
 * Run at send time, this applies the same allowlist to every url-shaped string at any depth,
 * absolute or a relative path, whatever its key, so no SDK default can carry a query secret
 * (a signed document url's `key`/`exp`/`sig`, an OAuth code) out.
 */
export function scrubAnalyticsEventProperties<T extends Record<string, unknown>>(properties: T): T {
  return scrubValue(null, properties, new WeakMap()) as T
}
